import { z } from 'zod';
import { Uuid } from './common.js';

/** Moteur de validation (CDC §3.4, GELÉ) : types repris à l'identique du contrat. */

/** Système touché par une étape, affiché dans la puce. */
export const PlanProvider = z.enum(['cockpit', 'axonaut', 'gmail', 'calendar', 'chat', 'trello', 'crisp']);
export type PlanProvider = z.infer<typeof PlanProvider>;

/** Libellé de la puce de chaque système (comme dans la maquette). */
export const PLAN_PROVIDER_LABELS: Record<PlanProvider, string> = {
  cockpit: 'Cockpit',
  axonaut: 'Axonaut',
  gmail: 'Gmail',
  calendar: 'Google Agenda',
  chat: 'Google Chat',
  trello: 'Trello',
  crisp: 'Crisp',
};

export const OperationType = z.enum([
  'cockpit.markActionDone',
  'cockpit.createAction',
  'cockpit.setActionStatus',
  'cockpit.scheduleAction',
  'cockpit.addProject',
  'cockpit.shiftProject',
  'cockpit.resizeProject',
  'cockpit.addProjectStep',
  // Lot BR : cocher ou décocher une étape (les étapes vivent dans le Cockpit), relier le client créé au projet.
  'cockpit.setProjectStepDone',
  'cockpit.linkProjectClient',
  'cockpit.setMeetingDate',
  'cockpit.confirmMeeting',
  // Lot U : ménage du planning (opérations internes).
  'cockpit.mergeProjects',
  'cockpit.deleteProject',
  'cockpit.removeProjectStep',
  'cockpit.linkProjectQuote',
  'cockpit.setQuoteProjectIgnored',
  'axonaut.createQuoteDraft',
  // Lot AT : client, contact et produit hors catalogue créés dans Axonaut avant le devis (jamais de facture).
  'axonaut.createCompany',
  'axonaut.createEmployee',
  'axonaut.createProduct',
  'gmail.sendEmail',
  'gmail.createDraft',
  // Lot BB : archiver un fil (retirer de la boîte de réception, marquer lu, libellé `Cockpit/Ignoré` si demandé).
  'gmail.archiveThread',
  'calendar.createEvent',
  'calendar.moveEvent',
  // Lot AF : inviter le client à l'événement que vient de créer l'étape précédente (case « Inviter le client »).
  'calendar.inviteAttendees',
  'chat.sendMessage',
  // Lot BB : note privée Crisp (visible de l'équipe seulement) ; hors plan, exception n° 4 (analyse prête, lot BD).
  // Lot BD : réponse au client (plan `support.reply`) : message opérateur Crisp, réponse dans le fil Gmail.
  'crisp.sendMessage',
  'gmail.reply',
  // Lot BD : statut d'une conversation du support (WAITING_CLIENT, DONE…), opération interne.
  'cockpit.setSupportStatus',
  // Lot BE : client touché par un problème connu noté « prévenu » (plan `support.notifyIssue`), opération interne.
  'cockpit.markIssueNotified',
]);
export type OperationType = z.infer<typeof OperationType>;

/**
 * Lot BR : Trello n'est plus utilisé. Ces opérations ne s'exécutent plus, mais les plans déjà enregistrés les citent :
 * ils restent lisibles dans l'historique (et un brouillon d'avant la bascule est refusé en clair à l'exécution).
 */
export const RETIRED_OPERATIONS = [
  'trello.createCard',
  'trello.addCheckItem',
  'trello.setCheckItemState',
  'trello.removeCheckItem',
] as const;
export const StoredOperationType = z.enum([...OperationType.options, ...RETIRED_OPERATIONS]);
export type StoredOperationType = z.infer<typeof StoredOperationType>;
export const isRetiredOperation = (op: StoredOperationType): op is (typeof RETIRED_OPERATIONS)[number] =>
  (RETIRED_OPERATIONS as readonly string[]).includes(op);

/**
 * Types de plans v1 (CDC §3.4). `demo.sample` est réservé au développement et aux tests
 * (FORCE_FAKE ou NODE_ENV ≠ production) : plan de démonstration à 3 étapes, ajouté avec l'accord du PM.
 */
export const PlanKind = z.enum([
  'demo.sample',
  'invoice.create',
  'quote.create',
  'reminder.email',
  'meeting.propose',
  'meeting.schedule',
  // Lot AF : « Fixer le R2 · <client> » (règle `meeting_r2_missing`) : créneau, invitation du client.
  'meeting.scheduleR2',
  // Lot BQ : confirmer une proposition de l'agenda : le rendez-vous reprend la date d'un événement existant.
  'meeting.confirm',
  'project.prepare',
  'project.shift',
  'project.resize',
  'project.addStep',
  'project.stepDone',
  // Lot U : fusionner, supprimer un projet ou une étape, rattacher ou ignorer un devis signé.
  'project.merge',
  'project.delete',
  'project.removeStep',
  'project.linkQuote',
  'project.ignoreQuote',
  // Lot BR : créer dans Axonaut le client d'un projet qui n'en a pas, puis le relier au projet.
  'project.createClient',
  'message.send',
  'action.done',
  'expense.categorize',
  // Lot BD : répondre à une conversation du support (Crisp ou Gmail), statut, archivage du fil, carte faite.
  'support.reply',
  // Lot BV : écarter une conversation, en archivant son fil Gmail si on le demande.
  'support.dismiss',
  // Lot BE : prévenir les clients touchés par un problème connu corrigé (réponse type, « prévenu », statut).
  'support.notifyIssue',
  // Lot BF : « Bloquer un créneau » d'une carte repoussée (prochain créneau libre de l'agenda du membre).
  'coach.block',
]);
export type PlanKind = z.infer<typeof PlanKind>;

/**
 * Lot BI : `SCHEDULED` = validé avec « Annuler » pendant quelques secondes (`deferSeconds`) : rien n'est encore
 * exécuté ; le serveur l'exécute à `scheduledFor`, sauf `POST /api/plans/:id/undo` avant (→ `CANCELLED`).
 */
export const PlanStatus = z.enum([
  'DRAFT',
  'SCHEDULED',
  'EXECUTING',
  'DONE',
  'PARTIAL',
  'FAILED',
  'CANCELLED',
]);
export type PlanStatus = z.infer<typeof PlanStatus>;

export const PlanChoice = z.object({ value: z.string(), label: z.string() });
export type PlanChoice = z.infer<typeof PlanChoice>;

export const PlanEditable = z.object({
  field: z.string(),
  label: z.string(),
  kind: z.enum(['text', 'textarea']),
});
export type PlanEditable = z.infer<typeof PlanEditable>;

export const PlanStep = z.object({
  /** Unique dans le plan, ex. « r3 ». */
  key: z.string().min(1),
  op: StoredOperationType,
  /** Affiché dans la puce. */
  via: PlanProvider,
  /** « Fixer le R3 le lundi 19 octobre à 10 h » */
  label: z.string(),
  detail: z.string().optional(),
  defaultChecked: z.boolean(),
  /** Le choix retenu est écrit dans `input.choice` avant validation (convention du moteur). */
  choices: z.array(PlanChoice).optional(),
  /** Champs modifiables : la valeur saisie remplace `input[field]` avant validation. */
  editable: z.array(PlanEditable).optional(),
  /** Validé par le schéma Zod de l'opération (registre du serveur). */
  input: z.record(z.string(), z.unknown()),
  /** Si une étape dont on dépend échoue ou est décochée, celle-ci est sautée. */
  dependsOn: z.array(z.string()).optional(),
});
export type PlanStep = z.infer<typeof PlanStep>;

export const Plan = z.object({
  id: Uuid,
  kind: PlanKind,
  title: z.string(),
  subtitle: z.string().optional(),
  steps: z.array(PlanStep),
  status: PlanStatus,
  context: z.record(z.string(), z.unknown()),
  /** Lot BI : échéance d'une exécution différée (ISO 8601), présente une fois le plan validé avec un délai. */
  scheduledFor: z.string().optional(),
});
export type Plan = z.infer<typeof Plan>;

/** `POST /api/plans` : le serveur construit le plan ; le client n'envoie jamais d'opérations. */
export const CreatePlanBody = z.object({
  kind: PlanKind,
  refs: z.record(z.string(), z.unknown()).default({}),
});
export type CreatePlanBody = z.input<typeof CreatePlanBody>;

export const PlanIdParams = z.object({ id: Uuid });

export const ExecuteStepChoice = z.object({
  key: z.string().min(1),
  checked: z.boolean(),
  choice: z.string().optional(),
  edits: z.record(z.string(), z.string()).optional(),
});
export type ExecuteStepChoice = z.infer<typeof ExecuteStepChoice>;

/** Lot BI : délai d'annulation proposé après une validation (« Annuler » pendant 5 secondes). */
export const PLAN_UNDO_SECONDS = 5;
export const PLAN_DEFER_MAX_SECONDS = 10;

/**
 * `POST /api/plans/:id/execute`. Une étape absente de la liste n'est pas exécutée :
 * seule une case explicitement cochée vaut validation. Lot BI : `deferSeconds` (0 à 10, 0 ou absent = exécution
 * immédiate) enregistre les choix, passe le plan en `SCHEDULED` et répond tout de suite ; le serveur l'exécute à
 * l'échéance, sauf annulation (`POST /api/plans/:id/undo`). Lot BQ : `gesture` : validation par le geste de
 * l'utilisateur (glisser une barre du planning), sans écran de validation : le serveur la refuse si le plan n'est pas
 * applicable directement (`context.direct`) ou si une étape cochée sort du Cockpit, et la trace à l'audit.
 */
export const ExecutePlanBody = z.object({
  steps: z.array(ExecuteStepChoice),
  deferSeconds: z.number().int().min(0).max(PLAN_DEFER_MAX_SECONDS).optional(),
  gesture: z.boolean().optional(),
});
export type ExecutePlanBody = z.infer<typeof ExecutePlanBody>;

/**
 * Lot BQ : `GET /api/plans/pending-moves` : plans de la personne connectée qui déplacent un projet ou fixent un
 * rendez-vous, validés et pas encore exécutés (leurs 5 secondes d'« Annuler ») ; `checked` : les étapes validées. Le
 * planning les relit au chargement : la barre reste à sa nouvelle place après un rechargement, « Annuler » reste offert.
 */
export const PendingMove = z.object({ plan: Plan, checked: z.array(z.string()) });
export type PendingMove = z.infer<typeof PendingMove>;
export const PendingMoveList = z.array(PendingMove);
export type PendingMoveList = z.infer<typeof PendingMoveList>;

export const StepResultStatus = z.enum(['ok', 'skipped', 'error']);
export type StepResultStatus = z.infer<typeof StepResultStatus>;

export const StepResult = z.object({
  key: z.string(),
  status: StepResultStatus,
  message: z.string().optional(),
  output: z.unknown().optional(),
});
export type StepResult = z.infer<typeof StepResult>;

/**
 * Lot T : carte de « À faire » ajoutée, fermée, rouverte ou réattribuée par les règles qui tournent juste
 * après l'exécution (conséquence du geste, pas l'opération elle-même). `reason` dit pourquoi
 * (« Prochaine étape du projet : … », « Fermée d'elle-même : étape du projet »).
 */
export const ConsequenceKind = z.enum(['created', 'closed', 'reopened', 'reassigned']);
export type ConsequenceKind = z.infer<typeof ConsequenceKind>;
export const ActionConsequence = z.object({
  kind: ConsequenceKind,
  actionId: Uuid,
  title: z.string(),
  assigneeId: Uuid.nullable(),
  /** Prénom affiché (« Stéphane ») ; null : personne d'attribué ou membre inconnu. */
  assigneeName: z.string().nullable(),
  /** Réattribution : la personne d'avant. */
  previousAssigneeId: Uuid.nullable().optional(),
  reason: z.string(),
});
export type ActionConsequence = z.infer<typeof ActionConsequence>;

/**
 * Réponse de `POST /api/plans/:id/execute`. Lot T : `consequences` (absent = aucune, ou règles en échec)
 * liste les cartes changées par les règles lancées après l'exécution.
 */
export const PlanRun = z.object({
  plan: Plan,
  results: z.array(StepResult),
  consequences: z.array(ActionConsequence).optional(),
  /**
   * Lot BI : exécution différée (`deferSeconds`) : plan `SCHEDULED`, `results` vide, échéance et délai réellement
   * appliqué (le serveur peut le raccourcir en test, `UNDO_SECONDS_OVERRIDE`).
   */
  scheduledFor: z.string().optional(),
  deferSeconds: z.number().int().optional(),
});
export type PlanRun = z.infer<typeof PlanRun>;

/** Événement SSE `plan.executed` (`GET /api/stream`). */
export const PlanExecutedEvent = z.object({
  planId: Uuid,
  kind: PlanKind,
  status: PlanStatus,
  executedBy: Uuid,
});
export type PlanExecutedEvent = z.infer<typeof PlanExecutedEvent>;

/**
 * Liens proposés par l'écran de validation (`plan.context.links`), ex. « Ouvrir Axonaut » quand la facture
 * se crée à la main (lot C1). Seuls les liens http(s) sont retenus.
 */
export const PlanLink = z.object({ label: z.string().min(1), url: z.url({ protocol: /^https?$/ }) });
export type PlanLink = z.infer<typeof PlanLink>;

/**
 * Mode d'emploi propre à un plan (`plan.context.guide`, liste d'étapes affichée à la place de la phrase
 * générique) et libellé du bouton de validation (`plan.context.submitLabel`). Ex. « Facturer dans Axonaut ».
 */
export function planGuide(context: Record<string, unknown> | undefined): string[] {
  const raw = context?.guide;
  return Array.isArray(raw)
    ? raw.filter((line): line is string => typeof line === 'string' && line.length > 0)
    : [];
}
export function planSubmitLabel(context: Record<string, unknown> | undefined): string {
  const raw = context?.submitLabel;
  return typeof raw === 'string' && raw.trim().length > 0 ? raw : 'Valider et exécuter';
}

/**
 * Date et heure modifiables d'un plan (`plan.context.slot`, lot K : « Fixer le R3 ») : l'écran de validation
 * affiche les deux champs ; les changer reconstruit le plan avec `refs.date` et `refs.time` (le recalcul des
 * dates suivantes dépend de la date choisie).
 */
export const PlanSlot = z.object({
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  time: z.string().regex(/^\d{2}:\d{2}$/),
  /** Période proposée (affichée à côté des champs). */
  windowStart: z.string().optional(),
  windowEnd: z.string().optional(),
  label: z.string().optional(),
});
export type PlanSlot = z.infer<typeof PlanSlot>;
export function planSlot(context: Record<string, unknown> | undefined): PlanSlot | null {
  const parsed = PlanSlot.safeParse(context?.slot);
  return parsed.success ? parsed.data : null;
}

/**
 * Lot U : avertissements d'un plan (`plan.context.alerts`), affichés en tête de l'écran de validation
 * (« Le client a déjà un projet en cours : … »). Clé distincte de
 * `context.warnings` de `project.resize` (déjà dans le détail de son étape).
 */
export function planAlerts(context: Record<string, unknown> | undefined): string[] {
  const raw = context?.alerts;
  return Array.isArray(raw)
    ? raw.filter((line): line is string => typeof line === 'string' && line.length > 0)
    : [];
}

export function planLinks(context: Record<string, unknown> | undefined): PlanLink[] {
  const raw = context?.links;
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((link) => {
    const parsed = PlanLink.safeParse(link);
    return parsed.success ? [parsed.data] : [];
  });
}

/**
 * Lot AL : type de projet modifiable dans l'écran « Préparer le projet » (`plan.context.projectType`). Le choisir
 * reconstruit le plan avec `refs.projectType` ; `detected` = type proposé d'office (client du canal « Partenaire
 * webdesign » → `partner`).
 */
export const PlanProjectType = z.object({
  value: z.enum(['evolusite', 'partner']),
  detected: z.enum(['evolusite', 'partner']),
  partnerName: z.string().nullable(),
});
export type PlanProjectType = z.infer<typeof PlanProjectType>;
export function planProjectType(context: Record<string, unknown> | undefined): PlanProjectType | null {
  const parsed = PlanProjectType.safeParse(context?.projectType);
  return parsed.success ? parsed.data : null;
}

/**
 * Lot AY : bloc « Sous-traitance » de « Préparer le projet » (`plan.context.subcontracting`) : le devis, et l'état
 * de « Maquette sous-traitée » dans les références du plan (coché d'office quand une ligne de maquette est
 * sous-traitée : le plan est reconstruit). La saisie est interne et enregistrée tout de suite, à part du plan.
 */
export const PlanSubcontracting = z.object({ quoteId: Uuid, subcontracted: z.boolean() });
export type PlanSubcontracting = z.infer<typeof PlanSubcontracting>;
export function planSubcontracting(context: Record<string, unknown> | undefined): PlanSubcontracting | null {
  const parsed = PlanSubcontracting.safeParse(context?.subcontracting);
  return parsed.success ? parsed.data : null;
}

/**
 * Lot AL : résumé des dates d'un plan (`plan.context.timeline`), affiché sous le choix du type : une ligne par
 * jalon (« Intégration · semaine du 26 oct. · signé le 14 oct. + 7 jours… »).
 */
export const PlanTimelineLine = z.object({
  label: z.string().min(1),
  value: z.string().min(1),
  note: z.string().optional(),
});
export type PlanTimelineLine = z.infer<typeof PlanTimelineLine>;
export function planTimeline(context: Record<string, unknown> | undefined): PlanTimelineLine[] {
  const raw = context?.timeline;
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((line) => {
    const parsed = PlanTimelineLine.safeParse(line);
    return parsed.success ? [parsed.data] : [];
  });
}

/**
 * Lot BE : étapes affichées GRISÉES « plus tard » dans l'écran de validation (`plan.context.laterSteps`), jamais
 * exécutables : ce ne sont pas des étapes du plan (une clé envoyée pour elles est refusée, `UNKNOWN_STEP`). Ex.
 * « Mettre à jour le site de … » (rebuild, lot BH) dans `support.notifyIssue`. `beforeKey` : l'étape avant laquelle
 * l'afficher (null : à la fin) ; `via` : libellé de la puce (« Back Evolusite »).
 */
export const PlanLaterStep = z.object({
  beforeKey: z.string().nullable(),
  label: z.string().min(1),
  detail: z.string().optional(),
  via: z.string().min(1),
});
export type PlanLaterStep = z.infer<typeof PlanLaterStep>;
export function planLaterSteps(context: Record<string, unknown> | undefined): PlanLaterStep[] {
  const raw = context?.laterSteps;
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((step) => {
    const parsed = PlanLaterStep.safeParse(step);
    return parsed.success ? [parsed.data] : [];
  });
}

/** Lot BE : clés des étapes affichées en retrait, qui complètent l'étape précédente (`plan.context.subSteps`). */
export function planSubSteps(context: Record<string, unknown> | undefined): string[] {
  const raw = context?.subSteps;
  return Array.isArray(raw) ? raw.filter((key): key is string => typeof key === 'string') : [];
}
